import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SpaceFolderEntry } from "../../hooks/useSpaceTree";
import { cssVar } from "../../utils/cssVar";
import { formatFileSize } from "../../utils/fileFormat";
import { usePressEffect } from "../fileCard";
import { FolderIcon } from "./folderIcon";

/**
 * Grid card for a subfolder, styled to match the share-link {@link FileCard}
 * shell exactly (same `#F7F7F7` surface, `174/204` aspect, 6px radius, drop
 * shadow, hover-lift and press feedback) so folders and files read as one grid.
 * In place of a thumbnail it shows the space folder glyph; the name bar reserves
 * the same height as a file card's (name + size) so the glyph reads at the same
 * size, even though only the folder name is shown.
 *
 * Single click highlights the folder (like a selected file card, no checkbox),
 * double click / Enter navigates in; on touch a single tap navigates directly.
 * Folders aren't selectable, so modified clicks do nothing.
 */
export const SpaceFolderCard = memo(function SpaceFolderCard({
  folder,
  onOpen,
  onFocusItem,
  openOnTap = false,
  focused = false,
}: {
  folder: SpaceFolderEntry;
  /** Navigate into the folder (double-click / Enter). */
  onOpen: (path: string[]) => void;
  /** Highlight the folder without navigating (single click). */
  onFocusItem: () => void;
  /** Touch: a single tap navigates (there is no highlight step). */
  openOnTap?: boolean;
  /** Controlled highlight, same fill + ring as a selected file card. */
  focused?: boolean;
}) {
  const { i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { isPressed, press } = usePressEffect();

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={folder.name}
      className="file-card relative flex select-none flex-col overflow-hidden outline-none"
      // Arms the CSS focus tint (index.css); absent while highlighted.
      data-highlight={focused ? undefined : "focus"}
      style={{
        aspectRatio: "174 / 204",
        borderRadius: "6px",
        cursor: "pointer",
        // Single-clicked folders use the selected card's fill + inset ring.
        backgroundColor: focused ? cssVar("--primary-light-color") : "#F7F7F7",
        boxShadow: `${
          isHovered
            ? "0px 6px 14px rgba(0,0,0,0.15)"
            : "0 3px 3px rgba(0,0,0,0.12)"
        }, inset 0 0 0 ${focused ? "2px" : "0px"} ${cssVar("--primary-color")}`,
        transform: `${
          isHovered && !isPressed ? "translateY(-1px)" : "translateY(0)"
        } scale(${isPressed ? 0.97 : 1})`,
        transition:
          "transform 0.12s ease-out, box-shadow 0.15s ease, background-color 0.15s ease",
      }}
      {...press}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        // Handled here, not by the listing's background-clear handler; the
        // file multi-select gestures do nothing on a folder.
        e.stopPropagation();
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        if (openOnTap) onOpen(folder.path);
        else onFocusItem();
      }}
      onDoubleClick={() => onOpen(folder.path)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter") {
          e.preventDefault();
          onOpen(folder.path);
        } else if (e.key === " ") {
          e.preventDefault();
          onFocusItem();
        }
      }}
    >
      {/* Keyboard-focus tint (CSS, index.css), painted under the content. */}
      <div
        aria-hidden
        className="file-card-focus-overlay pointer-events-none absolute inset-0"
      />
      {/* Thumbnail region — the space folder glyph sized to fill the region with
          the same padding the FileCard uses for its file-type icon, so the
          folder glyph reads at the same size as a file card's icon. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <div
          className="flex h-full w-full items-center justify-center"
          style={{ padding: "40px 40px 0 40px" }}
        >
          <FolderIcon fill />
        </div>
      </div>
      {/* Name bar — same geometry as FileCard's (name + size). */}
      <div className="flex shrink-0 items-center gap-2 px-[12%] py-2">
        <div
          className="flex min-w-0 flex-1 flex-col justify-center overflow-hidden"
          style={{ minHeight: 44 }}
        >
          <div className="w-full truncate text-[15px] font-normal leading-[22px]">
            {folder.name}
          </div>
          <div className="text-[12px] leading-[22px] text-[#747474]">
            {formatFileSize(folder.size, i18n.language)}
          </div>
        </div>
      </div>
    </div>
  );
});
